import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, CalendarClock, Receipt, Pencil, User, Clock, History } from "lucide-react";
import TimelineCalendar from "../components/TimelineCalendar";
import NewAppointmentDialog from "../components/NewAppointmentDialog";
import BillReport from "./BillReport";
import { istNow, ymd } from "../lib/dates";

const today = () => ymd(istNow());
const isoDay = (offset) => { const d = istNow(); d.setDate(d.getDate() + offset); return ymd(d); };
const mins = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const addMin = (hhmm, m) => { const t = mins(hhmm) + (Number(m) || 0); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };
const fmt12 = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); const hh = h % 12 === 0 ? 12 : h % 12; return `${hh}:${String(m).padStart(2, "0")}`; };
const dayName = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long" }); } catch { return ""; } };

// status → cell styling
const STATUS_STYLE = {
  Pending: "bg-secondary border-border text-foreground",
  Confirmed: "bg-primary/15 border-primary/50 text-foreground",
  Arrived: "bg-blue-500/15 border-blue-600/50 text-foreground",
  "In Service": "bg-amber-500/15 border-amber-600/50 text-foreground",
  Completed: "bg-green-500/15 border-green-700/50 text-foreground",
  Cancelled: "bg-red-500/10 border-red-800/60 text-red-300 line-through opacity-70",
  "No Show": "bg-red-500/10 border-red-800/60 text-red-300 line-through opacity-70",
};
const STATUS_BADGE = {
  Completed: "text-green-300 border-green-800", Cancelled: "text-red-300 border-red-800",
  "No Show": "text-red-300 border-red-800", Confirmed: "text-primary border-primary",
};
// Colour-code Appointment Book cells by service type for at-a-glance reading
const CAT_COLORS = [
  { label: "Hair Cut/Style", test: /hair ?cut|styl|blow|iron|tong|trim/i, color: "#6366F1" },
  { label: "Colour", test: /colou?r|highlight|global|root touch/i, color: "#EC4899" },
  { label: "Facial/Cleanup", test: /facial|clean ?up|de-?tan/i, color: "#10B981" },
  { label: "Spa/Massage", test: /spa|massage/i, color: "#06B6D4" },
  { label: "Mani/Pedi", test: /manicure|pedicure|nail/i, color: "#F59E0B" },
  { label: "Waxing", test: /wax|thread/i, color: "#A855F7" },
  { label: "Treatment", test: /keratin|treatment|smooth|botox|nano/i, color: "#14B8A6" },
  { label: "Makeup", test: /makeup|make-up|bridal/i, color: "#F43F5E" },
  { label: "Combo/Package", test: /combo|package/i, color: "#8B5CF6" },
];
const CAT_OTHER = "#9CA3AF";
const catTone = (a) => {
  const s = `${a.service_name || ""} ${a.category || ""}`;
  const m = CAT_COLORS.find((c) => c.test.test(s));
  return m ? m.color : CAT_OTHER;
};

export default function Billing() {
  const { branches, branchParam } = useBranch();
  const navigate = useNavigate();
  const branchObj = branches.find((b) => b.id === branchParam);
  const branchHours = { open: branchObj?.opening_time || "09:00", close: branchObj?.closing_time || "21:00" };
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [date, setDate] = useState(today());
  const [staffFilter, setStaffFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [bfaOpen, setBfaOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [apptInitial, setApptInitial] = useState(null);

  const openInlineAppt = (init) => { setApptInitial(init); setApptOpen(true); };

  const load = () => api.get("/appointments", { params: { branch_id: branchParam, date } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [branchParam, date]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/staff", { params: { branch_id: branchParam } }).then((r) => setStaff(r.data)).catch(() => {});
  }, [branchParam]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";
  const isAllBranches = !branchParam;

  // ---- time-slot columns from salon hours (15-min grid) ----
  const slots = useMemo(() => {
    const scope = branchParam ? branches.filter((b) => b.id === branchParam) : branches;
    let openM = Infinity, closeM = -Infinity;
    scope.forEach((b) => { openM = Math.min(openM, mins(b.opening_time || "11:30")); closeM = Math.max(closeM, mins(b.closing_time || "23:00")); });
    if (!isFinite(openM)) { openM = mins("11:30"); closeM = mins("23:00"); }
    const out = [];
    for (let t = openM; t < closeM; t += 15) out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    return out;
  }, [branches, branchParam]);

  // ---- staff rows (respect branch + staff filter) ----
  const gridStaff = useMemo(() => {
    const map = new Map();
    staff.filter((s) => s.status !== "inactive").forEach((s) => map.set(s.id, s));
    // include any staff referenced by appointments but missing from /staff (data drift)
    rows.forEach((a) => {
      const id = a.staff_id || "unassigned";
      if (!map.has(id)) map.set(id, { id, name: a.staff_name || "Unassigned", branch_id: a.branch_id });
    });
    let list = [...map.values()];
    if (staffFilter !== "all") list = list.filter((s) => s.id === staffFilter);
    return list;
  }, [staff, rows, staffFilter]);

  const apptsByStaff = useMemo(() => {
    const map = {};
    rows.forEach((a) => { const k = a.staff_id || "unassigned"; (map[k] = map[k] || []).push(a); });
    Object.values(map).forEach((arr) => arr.sort((x, y) => mins(x.start_time) - mins(y.start_time)));
    return map;
  }, [rows]);

  const billable = rows.filter((a) => a.billing_status !== "paid" && !["Cancelled", "No Show"].includes(a.status));

  // ---- Appointment → Bill (reuses POS prefill; POS links appt & marks billed on save) ----
  const createBill = (a) => {
    const group = a.booking_id ? rows.filter((r) => r.booking_id === a.booking_id) : [a];
    const svcs = group.map((g) => {
      const svc = services.find((s) => s.id === g.service_id);
      return { ...(svc || { id: g.service_id, name: g.service_name, category: "", price: 0, actual_price: 0, duration: g.duration || 30 }), staff_id: g.staff_id || "", staff_name: g.staff_name || "", freedom_group: g.freedom_group || null, freedom_slab_count: g.freedom_slab_count || null, freedom_slab_price: g.freedom_slab_price || null };
    });
    navigate("/billing/new", { state: { prefill: {
      appointmentId: group[0]?.id || a.id, appointmentIds: group.map((g) => g.id), booking_id: a.booking_id || null,
      branch_id: a.branch_id, customer_id: a.customer_id || null, customer_name: a.customer_name || "",
      customer_mobile: a.customer_mobile || "", services: svcs,
    } } });
  };

  const cellSpan = (a) => Math.max(1, Math.round((Number(a.duration) || 15) / 15));

  if (showHistory) return <div data-testid="billing-history-view"><BillReport onBack={() => setShowHistory(false)} /></div>;

  return (
    <div>
      <PageHeader title="Billing / POS" subtitle="Create bills manually or straight from the day's appointments"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setShowHistory(true)} data-testid="billing-history-btn" className="rounded-sm border-border">
              <History size={16} className="mr-1" /> Billing History
            </Button>
            <Button variant="outline" onClick={() => setBfaOpen(true)} data-testid="bill-from-appointment-btn" className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              <CalendarClock size={16} className="mr-1" /> Bill From Appointment
            </Button>
            <Button onClick={() => navigate("/billing/new")} data-testid="new-invoice-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">
              <Plus size={16} className="mr-1" /> New Bill
            </Button>
          </div>
        } />

      {/* Appointment Book (Timeline) header + date selector */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4" data-testid="appt-book-header">
        <div>
          <h2 className="font-display text-2xl">Appointment Book</h2>
          <p className="text-sm text-muted-foreground">{dayName(date)} · {date} · {isAllBranches ? "All Branches" : branchName(branchParam)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[["Today", isoDay(0)], ["Tomorrow", isoDay(1)], ["After Tomorrow", isoDay(2)]].map(([lbl, d]) => (
            <button key={lbl} onClick={() => setDate(d)} data-testid={`appt-date-${lbl.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${date === d ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{lbl}</button>
          ))}
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-book-date" className="h-9 w-40 bg-secondary" />
          <Button onClick={() => openInlineAppt({ branch_id: branchParam, date })} data-testid="appt-add-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37] h-9">
            <CalendarClock size={16} className="mr-1" /> Add Appointment
          </Button>
        </div>
      </div>

      {isAllBranches ? (
        <Card className="p-10 bg-card border-border text-center text-muted-foreground" data-testid="appt-book-selectbranch">
          Select a single branch (top-left) to view its operational timeline.
        </Card>
      ) : (
        <Card className="bg-card border-border p-0 overflow-hidden" data-testid="appointment-book">
          <TimelineCalendar
            branchId={branchParam}
            date={date}
            staff={staff.filter((s) => s.status !== "inactive")}
            appointments={rows}
            branchHours={branchHours}
            onSlotClick={(s, time, d) => openInlineAppt({ branch_id: branchParam, staff_id: s.id, date: d, start_time: time })}
            onApptClick={(a) => setDetail(a)}
          />
        </Card>
      )}

      {/* Inline New Appointment dialog — book straight from the POS timeline without leaving this page */}
      <NewAppointmentDialog open={apptOpen} onOpenChange={setApptOpen} branches={branches}
        services={services} staff={staff} initial={apptInitial} onSaved={() => load()} />

      {/* Appointment detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="book-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Appointment</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{detail.customer_name} · {detail.customer_mobile}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span>{detail.service_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Staff</span><span>{detail.staff_name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">When</span><span>{detail.date} · {fmt12(detail.start_time)}–{fmt12(detail.end_time || addMin(detail.start_time, detail.duration || 15))}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Branch</span><span>{branchName(detail.branch_id)}</span></div>
                <div className="flex justify-between items-center"><span className="text-muted-foreground">Status</span><Badge variant="outline" className={STATUS_BADGE[detail.status] || ""}>{detail.status}</Badge></div>
                <div className="flex justify-between items-center"><span className="text-muted-foreground">Billing</span>
                  {detail.billing_status === "paid"
                    ? <span className="text-green-300 text-xs">Billed{detail.bill_no ? ` · ${detail.bill_no}` : ""}{detail.payment_mode ? ` · ${detail.payment_mode}` : ""}</span>
                    : <span className="text-amber-300 text-xs">Not billed</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.billing_status === "paid" ? (
                  <Button size="sm" variant="outline" className="rounded-sm" disabled data-testid="detail-already-billed"><Receipt size={14} className="mr-1" /> Already Billed</Button>
                ) : ["Cancelled", "No Show"].includes(detail.status) ? (
                  <span className="text-xs text-muted-foreground">This appointment cannot be billed.</span>
                ) : (
                  <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" onClick={() => { const d = detail; setDetail(null); createBill(d); }} data-testid="detail-create-bill">
                    <Receipt size={14} className="mr-1" /> Create Bill
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => navigate("/appointments")} data-testid="detail-edit-appt"><Pencil size={14} className="mr-1" /> Edit Appointment</Button>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => navigate("/customers")} data-testid="detail-view-customer"><User size={14} className="mr-1" /> View Customer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bill From Appointment — quick pick */}
      <Dialog open={bfaOpen} onOpenChange={setBfaOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bfa-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Bill From Appointment</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">{dayName(date)} · {date} · {isAllBranches ? "All Branches" : branchName(branchParam)} — select an appointment to bill.</p>
          <div className="space-y-2">
            {billable.map((a) => (
              <button key={a.id} onClick={() => { setBfaOpen(false); createBill(a); }} data-testid={`bfa-appt-${a.id}`}
                className="w-full text-left rounded-sm border border-border bg-secondary hover:border-primary transition-colors p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{a.customer_name || "Walk-in"} <span className="text-muted-foreground">· {a.customer_mobile}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{a.service_name} · {a.staff_name || "—"}</div>
                  <div className="text-xs text-primary flex items-center gap-1 mt-0.5"><Clock size={11} /> {fmt12(a.start_time)}–{fmt12(a.end_time || addMin(a.start_time, a.duration || 15))}{isAllBranches ? ` · ${branchName(a.branch_id)}` : ""}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[a.status] || ""}>{a.status}</Badge>
              </button>
            ))}
            {!billable.length && <p className="text-center text-muted-foreground py-8 text-sm">No billable appointments for {date}.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
